import Axios from 'axios';
import * as ls from "local-storage";
import { setAllValue, getAuthPayloadSync } from '../globals/utils';
import UserInfo from '../models/UserInfo';
import { encryptionKey } from '../globals/constants'
import { SignJWT } from 'jose';

export class RestDataSource {
	constructor(baseurl) {
		this.BASE_URL = baseurl;
	}
	async SendRequest(method, url, callback, data = {}) {
		try {
			let req = Axios.request({
				method: method,
				url: url,
				data: data,
				withCredentials: true
			});

			const resp = (await req);
			const respdata = resp.data;
			if (resp.status === 200) {
				const { status, message } = respdata
				// For candidate/applicant login, accept message='success' even without status=true
				// Also handle cases where status might be false but message is success
				const isApplicantAuth = String(url).includes('applicantAuth');
				const isSuccess = (status === true && message === 'success') ||
					(isApplicantAuth && (message === 'success' || (status !== false && !respdata.error)));
				if (isSuccess) {
					if (String(url).search('login/auth') !== -1 || String(url).search('login/applicantAuth') !== -1 || String(url).search('login/vendorAuth') !== -1) {
						// Handle candidate login response structure - data might be nested or flat
						const candidateData = respdata.data || respdata;
						const { valid, isBlocked } = candidateData;
						let isAdminOrChp = false;
						let isLoggedByOperations = false;
						let roleCode = '';
						let rolePriority = '';

						const isApplicantLogin = String(url).includes('login/applicantAuth') || String(url).includes('login/applicant');

						// derive roleCode/priority
						if (isApplicantLogin) {
							roleCode = 'candidate';
							rolePriority = '';
						} else {
							if (candidateData.roleCodeList && candidateData.roleCodeList.length === 1) {
								roleCode = candidateData.roleCodeList[0];
							} else {
								roleCode = candidateData.roleCode || '';
							}
							if (candidateData.rolePriorityList && candidateData.rolePriorityList.length) {
								if (candidateData.rolePriorityList.length === 1) {
									rolePriority = candidateData.rolePriorityList[0];
								}
								// admin/chp detection from priority list and roleMap
								const rpList = candidateData.rolePriorityList;
								const rm = candidateData.roleMap || {};
								if (rm['admin'] !== undefined && rpList.includes(rm['admin'])) isAdminOrChp = true;
								if (rm['operationshead'] !== undefined && rpList.includes(rm['operationshead'])) isAdminOrChp = true;
								if (rm['chp'] !== undefined && rpList.includes(rm['chp'])) isAdminOrChp = true;
								if (rm['operations'] !== undefined && rpList.includes(rm['operations'])) isLoggedByOperations = true;
							}
							const roleCodeNorm = (roleCode || '').toLowerCase();
							// additional fallback: if roleCode is admin/operationshead/chp then flag
							if (['admin', 'operationshead', 'chp'].includes(roleCodeNorm)) {
								isAdminOrChp = true;
							}
						}
						let roleId = '';
						if (roleCode !== '' && candidateData.roleList && Array.isArray(candidateData.roleList)) {
							candidateData.roleList.forEach(data => {
								if ((data.code || '').toLowerCase() === (roleCode || '').toLowerCase())
									roleId = data.id;
							})
						}
						if (isApplicantLogin || (candidateData.uploadBy && candidateData.uploadBy === 'candidate')) {
							roleCode = 'candidate'
						}

						// For candidate/applicant login, set isloggedin=true immediately
						// CandidateLoginLayout.loginSuccess() will also set it, but this ensures it's set early
						const token = await setAllValue({
							'isloggedin': true,
							'isForgotPassword': false,
							'userId': isApplicantLogin ? (candidateData.userId || candidateData.applicantId) : candidateData.userId,
							'username': candidateData.username,
							'clientId': candidateData.clientId,
							'clientLogo': candidateData.clientLogo,
							'applicantId': candidateData.applicantId,
							'firstTimeLogin': candidateData.firstTimeLogin,
							'loginname': candidateData.firstname,
							'clientEmail': candidateData.clientEmail,
							'otpExpiryTime': candidateData.otpExpiryTime,
							'mobilenumber': candidateData.mobilenumber,
							'vendorId': candidateData.vendorId,
							'authenticatorEnabled': candidateData.authenticatorEnabled,
							'accessibleTabs': candidateData.accessibleTabs,
							'accessExpiryTime': candidateData.accessExpiryTime,
							'showBilling': candidateData.showBilling,
							'faceIdEnabled': true,
							'roleList': candidateData.roleList,
							'rolePriorityList': candidateData.rolePriorityList,
							'roleCodeList': isApplicantLogin ? ['candidate'] : candidateData.roleCodeList,
							'roleMap': candidateData.roleMap,
							'isLoggedByOperations': isLoggedByOperations,
							'rolePath': '',
							'rolePriority': rolePriority,
							'isAdminOrChp': isAdminOrChp,
							'roleCode': roleCode,
							// prefer roleId from response; fallback to derived roleId if single role
							'roleId': candidateData.roleId || roleId,
							'clientCode': candidateData.clientCode,
							'chatBotUrl': candidateData.chatBotUrl,
							'metaPyraUrl': candidateData.metaPyraUrl,

						});

						// OWASP A02: Do not store JWT on frontend; auth is via HttpOnly cookies only. Set UserInfo from response payload.
						if (isApplicantLogin || (valid !== false && isBlocked !== true)) {
							const name = candidateData.firstname || candidateData.loginname || '';
							const role = candidateData.roleCode || '';
							const userid = candidateData.userId ?? candidateData.applicantId;
							if (candidateData.username != null) {
								try {
									UserInfo.setUserDetail({
										username: candidateData.username,
										extradata: { name, role, userid }
									}, true);
								} catch (_) {}
							}
						} else {
							console.log('Invalid Credentials')
						}
					}
				} else {
					// Log response structure for debugging
					if (String(url).includes('applicantAuth')) {
						console.log('Candidate login response (not success):', {
							status,
							message,
							error: respdata.error,
							hasData: !!respdata.data,
							fullResponse: respdata
						});
						// If status is true but message is not 'success', it might be an error message
						if (status === true && message && message !== 'success') {
							console.log('Login failed with message:', message);
						}
					} else {
						console.log('Regular flow');
					}
				}
			}
			callback(resp.data);
		} catch (err) {
			console.log('Error in response', err);
			
			// Check for 401 Unauthorized (session expired/invalidated)
			if (err.response && err.response.status === 401) {
				const errorData = err.response.data || {};
				const errorMessage = errorData.message || errorData.error || 'Session expired or invalid';
				
				// Check if this is a session invalidation (not just invalid credentials)
				// Session invalidation typically happens after logout or concurrent login
				if (errorMessage.includes('Session expired') || 
				    errorMessage.includes('Session invalid') ||
				    errorMessage.includes('Please login again')) {
					
					const payload = getAuthPayloadSync();
					if (payload) {
						// Show concurrent login dialog if session was invalidated while user was active
						console.log('Session invalidated - showing dialog');
						if (typeof window !== 'undefined') {
							window.dispatchEvent(new CustomEvent('showSessionDialog', {
								detail: { type: 'concurrent' }
							}));
						}
					}
				}
			}
			
			if (err.response && err.response.data) {
				callback(err.response.data);
			} else if (err.message) {
				callback({ error: err.message });
			} else {
				callback({ error: "Unknown Error" });
			}
		}
	}
	async GetData(callback, data = {}, method = "get") {
		this.SendRequest(method, this.BASE_URL, callback, data);
	}
	async GetOne(callback, id) {
		this.SendRequest("get", `{this.BASE_URL}/${id}`, callback);
	}

	async Save(callback, data) {
		this.SendRequest("post", this.BASE_URL, callback, data);
	}

	async Update(callback, data) {
		this.SendRequest("put", `{this.BASE_URL}/${data.id}`, callback, data);
	}

	async Delete(callback, data) {
		this.SendRequest("delete", `{this.BASE_URL}/${data.id}`, callback, data);
	}
}